import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, ShieldAlert, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart, itemPrice, itemPack, itemKey } from "@/context/CartContext";
import { inr } from "@/lib/api";
import { waLink } from "@/lib/config";

export default function CartDrawer() {
  const { items, open, setOpen, setQty, remove, total, hasRx } = useCart();
  const navigate = useNavigate();

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-[#0B0F17]/40 backdrop-blur-sm z-[70]"
            data-testid="cart-drawer-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-[80] flex flex-col border-l hairline"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b hairline">
              <h2 className="font-display font-bold text-lg tracking-tight">Your Cart</h2>
              <button data-testid="cart-drawer-close" onClick={() => setOpen(false)} className="p-2 hover:bg-slate-100 transition-colors"><X size={18} /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <p className="text-slate-400 text-sm py-16 text-center font-mono-ed uppercase tracking-[0.2em]" data-testid="cart-empty-msg">Cart is empty</p>
              ) : (
                items.map((item) => {
                  const { product, qty } = item;
                  const key = itemKey(item);
                  return (
                  <div key={key} className="flex gap-4 py-4 border-b hairline" data-testid={`cart-item-${key}`}>
                    <div className="w-16 h-20 bg-[#F1F5F9] overflow-hidden flex-shrink-0">
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-2">
                        <p className="font-semibold text-sm truncate">{product.name}</p>
                        <button data-testid={`cart-remove-${key}`} onClick={() => remove(key)} className="text-slate-400 hover:text-red-600 transition-colors"><X size={14} /></button>
                      </div>
                      <p className="font-mono-ed text-[10px] uppercase tracking-wider text-slate-400 mt-0.5" data-testid={`cart-boxes-${key}`}>{item.qty * (parseInt(itemPack(item), 10) || 1)} boxes ({(item.qty * (parseInt(itemPack(item), 10) || 1) * 10).toLocaleString("en-IN")} strips)</p>
                      {(() => { const bx = item.qty * (parseInt(itemPack(item), 10) || 1); const fb = bx >= 10 ? Math.floor(bx / 2) : 0; return fb > 0 ? <p className="font-mono-ed text-[9px] text-emerald-700 font-semibold mt-0.5" data-testid={`cart-free-${key}`}>+ {fb} boxes FREE (scheme)</p> : null; })()}
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center border hairline">
                          <button data-testid={`cart-qty-minus-${key}`} onClick={() => setQty(key, qty - 1)} className="px-2 py-1 hover:bg-slate-100"><Minus size={12} /></button>
                          <span className="px-2 text-sm font-mono-ed">{qty}</span>
                          <button data-testid={`cart-qty-plus-${key}`} onClick={() => setQty(key, qty + 1)} className="px-2 py-1 hover:bg-slate-100"><Plus size={12} /></button>
                        </div>
                        <span className="font-mono-ed text-sm font-semibold">{inr(itemPrice(item) * qty)}</span>
                      </div>
                    </div>
                  </div>
                  );
                })
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t hairline px-6 py-5 space-y-4">
                {hasRx && (
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 px-3 py-2.5 rounded-lg" data-testid="cart-rx-warning">
                    <ShieldAlert size={15} className="text-amber-700 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-900 leading-relaxed">Your cart contains Rx medicines. As a licensed B2B buyer no prescription is needed — your DL & GST numbers are collected right after payment.</p>
                  </div>
                )}
                <div className="flex justify-between font-mono-ed text-sm">
                  <span className="uppercase tracking-[0.2em] text-slate-500">Subtotal (Net)</span>
                  <span className="font-semibold" data-testid="cart-subtotal">{inr(total)}</span>
                </div>
                <p className="text-[10px] text-slate-400 font-mono-ed">+ 5% GST calculated at checkout</p>
                {(() => { const fb = items.reduce((s, i) => { const bx = i.qty * (parseInt(itemPack(i), 10) || 1); return s + (bx >= 10 ? Math.floor(bx / 2) : 0); }, 0); return fb > 0 ? <p className="font-mono-ed text-[11px] font-semibold text-emerald-700" data-testid="cart-free-boxes">Scheme benefit: +{fb} boxes FREE (₹0.00) with this order</p> : null; })()}
                <button
                  data-testid="cart-checkout-btn"
                  onClick={() => { setOpen(false); navigate("/checkout"); }}
                  className="w-full bg-[#0F5A47] text-white py-3.5 font-mono-ed text-xs uppercase tracking-[0.25em] hover:bg-[#0A4234] transition-colors duration-300 active:scale-[0.98]"
                >
                  Proceed to Checkout
                </button>
                <a
                  data-testid="cart-whatsapp-order-btn"
                  href={waLink(
                    "Hello Kanan Remedies, I'd like to place a wholesale order:\n\n" +
                    items.map((i) => `• ${i.product.name} (${itemPack(i)}) × ${i.qty} — ${inr(itemPrice(i) * i.qty)}`).join("\n") +
                    `\n\nOrder Total: ${inr(total)}\n\nPlease confirm availability and delivery timeline.`
                  )}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-white py-3.5 font-mono-ed text-xs uppercase tracking-[0.25em] hover:bg-[#1EBE5B] transition-colors duration-300 active:scale-[0.98]"
                >
                  <MessageCircle size={15} /> Order via WhatsApp
                </a>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
